const TOKEN_PATTERN = /^[!#$%&'*+\-.^_`|~0-9A-Za-z]+$/u;
const BASE64_PATTERN = /^[A-Za-z0-9+/]*={0,2}$/u;
const MANAGED_EMAIL_HEADER_NAMES = new Set([
	"bcc",
	"cc",
	"content-transfer-encoding",
	"content-type",
	"date",
	"from",
	"message-id",
	"mime-version",
	"reply-to",
	"subject",
	"to",
]);

export function hasControlCharacters(value: string): boolean {
	return /[\u0000-\u001f\u007f]/u.test(value);
}

/**
 * Checks whether a value is a valid RFC 5322 header field name.
 *
 * @param value - Header field name to check.
 * @returns Whether the value contains only token characters.
 */
export function isHeaderName(value: string): boolean {
	return TOKEN_PATTERN.test(value);
}

/**
 * Checks for control characters that cannot safely appear in a custom header.
 * LF and CRLF are allowed so callers can provide folded multiline values.
 *
 * @param value - Header value to check.
 * @returns Whether the value contains an unsafe control character.
 */
export function hasInvalidHeaderValueCharacters(value: string): boolean {
	for (let index = 0; index < value.length; index++) {
		const codePoint = value.charCodeAt(index);
		if (codePoint === 10) {
			continue;
		}
		if (codePoint === 13 && value.charCodeAt(index + 1) === 10) {
			index += 1;
			continue;
		}
		if (codePoint < 32 || codePoint === 127) {
			return true;
		}
	}
	return false;
}

/**
 * Checks whether a header is generated by the email composer.
 *
 * @param value - Header name to check.
 * @returns Whether the composer owns the header value.
 */
export function isManagedEmailHeaderName(value: string): boolean {
	return MANAGED_EMAIL_HEADER_NAMES.has(value.toLowerCase());
}

/**
 * Folds line breaks in a custom header into MIME continuation lines.
 *
 * @param value - Validated custom header value.
 * @returns A header value with canonical CRLF continuation lines.
 */
export function foldHeaderValue(value: string): string {
	return value.replace(/\r?\n/gu, "\r\n ");
}

export function isMimeType(value: string): boolean {
	const separator = value.indexOf("/");
	return (
		separator > 0 &&
		separator === value.lastIndexOf("/") &&
		TOKEN_PATTERN.test(value.slice(0, separator)) &&
		TOKEN_PATTERN.test(value.slice(separator + 1))
	);
}

export function normalizeBase64(value: string): string | undefined {
	const normalized = value.replace(/\s/gu, "");
	if (normalized.length % 4 !== 0 || !BASE64_PATTERN.test(normalized)) {
		return undefined;
	}
	try {
		atob(normalized);
		return normalized;
	} catch {
		return undefined;
	}
}
